import React, { useEffect, useCallback, useState } from 'react';
import useLocationSearchParams from '@medflex/hooks/useLocationSearchParams';
import { useTranslation } from 'react-i18next';
import Modal from '@medflex/components/stories/Modal';
import { DeleteSettings } from '@medflex/components/stories/Sessions/SessionsDetailModal';
import debounce from 'lodash/debounce';
import { trackEvent } from '@medflex/utils/Tracking';
import { showAlertToast, showSuccessToast } from '@medflex/components/stories/Toast';
import useModal from '../../hooks/useModal';
import useSessionChat from '../../hooks/useSessionChat';
import useSessionRoles from '../../hooks/useSessionRoles';

const SessionChatDeleteSettingsModule = () => {
  const { close, open } = useModal();
  const { id } = useLocationSearchParams();
  const { t } = useTranslation('messenger');
  const { chat, update, status } = useSessionChat({ id });
  const { isModerator, isOrganizer } = useSessionRoles(chat ? chat.participants : undefined);
  const { expires } = chat || {};
  const [optimisticExpires, setOptimisticExpires] = useState(expires);
  const [deleteSettingsUpdating, setDeleteSettingsUpdating] = useState(false);

  useEffect(() => {
    if (status.loadChat === 'succeeded') {
      setOptimisticExpires(expires);
    }
  }, [status]);

  const setExpires = useCallback(
    debounce(async (newExpiresValue, event) => {
      try {
        await update({ expires: newExpiresValue });
        trackEvent(event, { elementType: 'expires', actionText: newExpiresValue });
        showSuccessToast(t('SessionChatDetailModal.expiresChangedSuccessMessage'), null, {
          toastId: 'sessionExpiresSuccess',
        });
      } catch (e) {
        setOptimisticExpires(expires);
        showAlertToast(t('SessionChatDetailModal.expiresChangedUnexpectedError'), {
          toastId: 'sessionExpiresFailed',
        });
      } finally {
        setDeleteSettingsUpdating(false);
      }
    }, 1000),
    [expires],
  );

  const closeModal = () => close({ modal: 'sessionChatDeleteSettings', id });

  useEffect(() => {
    if (!id) closeModal();
    if (chat && !(isModerator || isOrganizer)) closeModal();
  }, [id, chat, isModerator, isOrganizer]);

  const handleExpiresToggled = event => {
    setDeleteSettingsUpdating(true);
    const newExpiresValue = !optimisticExpires;
    setOptimisticExpires(newExpiresValue);
    setExpires(newExpiresValue, event);
  };

  const handleDeleteMessages = () => {
    open({ modal: 'sessionChatDeleteMessages', id });
  };

  const handleDeleteChat = () => {
    open({ modal: 'sessionChatRemove', id });
  };

  return (
    <Modal
      showCloseButton
      onClickCloseButton={() => {
        closeModal();
      }}
      title={t('components:Sessions.SessionsDetailModal.SessionsDetailDeleteSettings.title')}
    >
      <div data-component="SessionChatDeleteSettingsModule" className="flex flex-col">
        <DeleteSettings
          expires={optimisticExpires}
          onExpireToggled={handleExpiresToggled}
          onDeleteMessages={handleDeleteMessages}
          loading={deleteSettingsUpdating}
          onDeleteChat={handleDeleteChat}
        />
      </div>
    </Modal>
  );
};

export default SessionChatDeleteSettingsModule;
